import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router-dom";

import { ErrorState, Notice, PageHeader, RouteTabs, SkeletonRows } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { useBusinessAuth } from "../../../context/BusinessAuthContext";
import { LeaveRequestModal, OvertimeModal } from "../components/LeaveOvertimeModals";
import { CorrectionRequestModal, ScheduleRequestModal } from "../components/MyHrParts";
import { MY_HR_SECTIONS, legacyTabPath } from "./selfService";

// My HR (ESS-5, UI-3C): the signed-in employee's own work — today, schedule, attendance, leave, overtime, requests
// and pay — as one workspace with a real address per section. Every call is "me": the server resolves the employee
// from the session and no member id is sent. The request forms open from any section and are owned here, so a sent
// request refreshes whatever is on screen.

const SENT = {
  correction: "Correction sent. Someone else reviews it; the decision appears in Requests.",
  schedule: "Schedule request sent. A schedule manager reviews it; the decision appears in Requests.",
  leave: "Leave request sent. Someone who reviews leave decides it; the decision appears in Leave.",
  overtime: "Overtime request sent. Someone who approves overtime decides it; the decision appears in Overtime.",
};

export default function MyHRLayout() {
  const auth = useBusinessAuth();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ status: "loading", overview: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState(null); // { kind, data }
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const result = await businessApi.getMyHROverview();
      setState(result?.overview ? { status: "ready", overview: result.overview } : { status: "error", overview: null });
    } catch {
      setState((current) => (current.overview ? current : { status: "error", overview: null }));
    }
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);
  const { overview } = state;
  const today = overview?.todayDate;

  const context = useMemo(() => ({
    overview,
    today,
    reloadKey,
    refresh,
    notify: setNotice,
    askCorrection: (day) => setForm({ kind: "correction", data: day }),
    askSchedule: (week = null) => setForm({ kind: "schedule", data: week }),
    askLeave: (leave) => setForm({ kind: "leave", data: leave }),
    askOvertime: (initial = {}) => setForm({ kind: "overtime", data: initial }),
  }), [overview, today, reloadKey, refresh]);

  const legacy = legacyTabPath(searchParams.get("tab"));
  if (legacy) return <Navigate to={legacy} replace />;

  const close = () => setForm(null);
  const sent = (kind) => () => {
    setForm(null);
    setNotice(SENT[kind]);
    refresh();
  };

  const tabs = MY_HR_SECTIONS.map((section) => ({
    ...section,
    count: section.key === "requests" ? overview?.pendingRequests : section.key === "attendance" ? overview?.attention?.length : undefined,
  }));
  const who = [overview?.role, auth?.businessName].filter(Boolean).join(" at ");

  return (
    <div className="w-full max-w-form space-y-6">
      <PageHeader title="My HR" description={who || undefined} navigation={<RouteTabs items={tabs} label="My HR sections" />} />

      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}

      {state.status === "loading" ? (
        <div className="rounded-panel border border-default bg-surface"><SkeletonRows rows={5} columns={3} label="Loading My HR" /></div>
      ) : state.status === "error" ? (
        <div className="rounded-panel border border-default bg-surface">
          <ErrorState title="My HR couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />
        </div>
      ) : (
        <Outlet context={context} />
      )}

      {form?.kind === "correction" ? <CorrectionRequestModal day={form.data} onClose={close} onSubmitted={sent("correction")} /> : null}
      {form?.kind === "schedule" && today ? <ScheduleRequestModal today={today} currentWeek={form.data} onClose={close} onSubmitted={sent("schedule")} /> : null}
      {form?.kind === "leave" ? <LeaveRequestModal balances={form.data?.balances} types={form.data?.types} onClose={close} onSubmitted={sent("leave")} /> : null}
      {form?.kind === "overtime" ? <OvertimeModal initial={form.data} onClose={close} onSubmitted={sent("overtime")} /> : null}
    </div>
  );
}
